import { z } from 'zod';

export const lotStatusSchema = z.enum(['ACTIVE', 'BLOCKED', 'EXHAUSTED', 'ARCHIVED']);
export type LotStatus = z.infer<typeof lotStatusSchema>;

export const lotOriginSchema = z.enum(['DE', 'ES', 'ZE', 'CLA', 'OTHER']);
export type LotOrigin = z.infer<typeof lotOriginSchema>;

// Mercato destinazione preferito del lotto (Drive MPG.RESIDUE separa ES da FR).
export const lotTargetCountrySchema = z.enum(['ES', 'FR']);
export type LotTargetCountry = z.infer<typeof lotTargetCountrySchema>;

// Lot code: regex flessibile per accettare lotti storici + canonico.
// Pattern canonico (Apps Script utils.gs): [DE|ES|ZE|CLA]NNNDDMMYY (max 11 chars).
// Accettiamo anche identificatori liberi (uppercase + numeri + _ -) per import storici.
const lotCodeSchema = z
  .string()
  .min(1, 'Codice lotto obbligatorio')
  .max(64, 'Codice lotto max 64 caratteri')
  .regex(/^[A-Z0-9_-]+$/i, 'Codice puo contenere solo lettere, numeri, _ e -');

const isoDateOptional = z
  .string()
  .datetime({ message: 'Data deve essere ISO 8601' })
  .nullable()
  .optional();

// Create / Update

export const lotCreateRequestSchema = z
  .object({
    code: lotCodeSchema,
    origin: lotOriginSchema.default('OTHER'),
    productId: z.string().uuid({ message: 'productId deve essere UUID' }),
    status: lotStatusSchema.default('ACTIVE'),
    residualQty: z.coerce.number().min(0, 'Quantita residua deve essere >= 0').default(0),
    blockedReason: z.string().max(500).nullable().optional(),
    receivedAt: isoDateOptional,
    parentLotId: z.string().uuid().nullable().optional(),
    targetCountry: lotTargetCountrySchema.nullable().optional(),
  })
  .refine(
    (data) => {
      // Se status BLOCKED, blockedReason e' obbligatorio
      if (data.status === 'BLOCKED' && !data.blockedReason?.trim()) return false;
      return true;
    },
    {
      message: 'Quando il lotto e BLOCKED, indicare il motivo del blocco',
      path: ['blockedReason'],
    },
  );
export type LotCreateRequest = z.infer<typeof lotCreateRequestSchema>;

// Update partial: tutti i campi opzionali
const lotUpdateBase = z.object({
  code: lotCodeSchema.optional(),
  origin: lotOriginSchema.optional(),
  productId: z.string().uuid().optional(),
  status: lotStatusSchema.optional(),
  residualQty: z.coerce.number().min(0).optional(),
  blockedReason: z.string().max(500).nullable().optional(),
  receivedAt: isoDateOptional,
  exhaustedAt: isoDateOptional,
  parentLotId: z.string().uuid().nullable().optional(),
  targetCountry: lotTargetCountrySchema.nullable().optional(),
});

export const lotUpdateRequestSchema = lotUpdateBase.refine(
  (data) => {
    if (data.status === 'BLOCKED' && data.blockedReason !== undefined && !data.blockedReason?.trim()) {
      return false;
    }
    return true;
  },
  {
    message: 'Quando il lotto e BLOCKED, indicare il motivo del blocco',
    path: ['blockedReason'],
  },
);
export type LotUpdateRequest = z.infer<typeof lotUpdateRequestSchema>;

// Response

export const lotResponseSchema = z.object({
  id: z.string(),
  code: z.string(),
  origin: lotOriginSchema,
  productId: z.string(),
  productCode: z.string(),
  productName: z.string(),
  productUnit: z.string(),
  status: lotStatusSchema,
  residualQty: z.number(),
  blockedReason: z.string().nullable(),
  receivedAt: z.string().nullable(),
  exhaustedAt: z.string().nullable(),
  parentLotId: z.string().nullable(),
  parentLotCode: z.string().nullable(),
  targetCountry: lotTargetCountrySchema.nullable(),
  currentZoneId: z.string().nullable(),
  currentZoneCode: z
    .enum(['RICEVIMENTO_QUARANTENA', 'PRINCIPALE', 'PRODUZIONE', 'PRODOTTO_FINITO', 'DA_RESTITUIRE'])
    .nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type LotResponse = z.infer<typeof lotResponseSchema>;

export const lotListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
  search: z.string().trim().min(1).max(100).optional(),
  status: lotStatusSchema.optional(),
  origin: lotOriginSchema.optional(),
  productId: z.string().uuid().optional(),
  targetCountry: lotTargetCountrySchema.optional(),
});
export type LotListQuery = z.infer<typeof lotListQuerySchema>;

export const lotListResponseSchema = z.object({
  items: z.array(lotResponseSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
});
export type LotListResponse = z.infer<typeof lotListResponseSchema>;

// Labels UI IT

export const LOT_STATUS_LABELS_IT: Record<LotStatus, string> = {
  ACTIVE: 'Attivo',
  BLOCKED: 'Bloccato',
  EXHAUSTED: 'Esaurito',
  ARCHIVED: 'Archiviato',
};

export const LOT_ORIGIN_LABELS_IT: Record<LotOrigin, string> = {
  DE: 'DE',
  ES: 'ES',
  ZE: 'ZE',
  CLA: 'CLA',
  OTHER: 'Altro',
};

export const LOT_TARGET_COUNTRY_LABELS_IT: Record<LotTargetCountry, string> = {
  ES: 'Spagna',
  FR: 'Francia',
};
